import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { CARD_CODES } from '@app/shared';
import type { CardPaymentInput, PaymentConfigDto, SavedCardDto } from '@app/shared';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { AuthorizeNetCard } from './AuthorizeNetCard';

/**
 * The card part of a payment screen (switch `card-payments`). A new card is typed into the gateway's
 * own form (Stripe or Authorize.Net), which gives a token; the number never reaches 212Ez. On a test system without a gateway,
 * a simulated test card is chosen instead. The screen calls `collect()` on submit.
 */
export interface CardFieldHandle {
  collect: () => Promise<CardPaymentInput>;
}

export const paymentConfigKey = ['payments', 'config'];

/** The card form's configuration, while card payments are released. */
export function usePaymentConfig(path = '/payments/config') {
  const featureOn = useFeatureReleased();
  const on = featureOn('card-payments');
  return useQuery({
    queryKey: [...paymentConfigKey, path],
    queryFn: () => api.get<PaymentConfigDto>(path),
    enabled: on,
    staleTime: 60_000,
  });
}

/** Whether the screens may offer Credit Card: released and a gateway (or its test simulation). */
export function useCardsAvailable(): boolean {
  const featureOn = useFeatureReleased();
  const config = usePaymentConfig();
  return featureOn('card-payments') && !!config.data && config.data.gateway !== 'none';
}

const stripes = new Map<string, Promise<Stripe | null>>();
const stripeFor = (key: string) => {
  if (!stripes.has(key)) stripes.set(key, loadStripe(key));
  return stripes.get(key)!;
};

const NEW = 'new';
const SIM_CARDS = [
  { token: 'pm_card_visa', label: 'Test card that is approved (VISA ending 4242)' },
  { token: 'pm_card_chargeDeclined', label: 'Test card that is declined' },
];

interface Props {
  /** The customer, for saved cards and "Save this card"; absent at enrollment. */
  customerId?: string;
  error?: string;
  /** On the customer portal: its own addresses, instead of the staff ones. */
  portal?: { configPath: string; cardsPath: string };
}

function StripeCard({ handle, props }: { handle: Ref<CardFieldHandle>; props: Props & Shared }) {
  const stripe = useStripe();
  const elements = useElements();
  useImperativeHandle(handle, () => ({
    collect: async () => {
      if (props.choice !== NEW) return { savedCardId: props.choice, save: false };
      const card = elements?.getElement(CardElement);
      if (!stripe || !card) throw new Error('The card form is still loading.');
      const { paymentMethod, error } = await stripe.createPaymentMethod({ type: 'card', card });
      if (error || !paymentMethod) throw new Error(error?.message ?? 'Check the card details.');
      return { paymentMethodId: paymentMethod.id, save: props.save };
    },
  }));
  return props.choice === NEW ? (
    <Box
      aria-label="Card details"
      sx={{
        border: 1,
        borderColor: props.error ? 'error.main' : 'divider',
        borderRadius: 1,
        p: 1.25,
      }}
    >
      <CardElement options={{ hidePostalCode: false }} />
    </Box>
  ) : null;
}

interface Shared {
  choice: string;
  save: boolean;
}

export const CardField = forwardRef<CardFieldHandle, Props>(function CardField(props, ref) {
  const { can } = useAuth();
  const config = usePaymentConfig(props.portal?.configPath);
  const saved = useQuery({
    queryKey: props.portal
      ? ['portal', props.portal.cardsPath]
      : ['customers', props.customerId, 'cards'],
    queryFn: () =>
      api.get<{ data: SavedCardDto[] }>(
        props.portal?.cardsPath ?? `/customers/${props.customerId}/cards`,
      ),
    select: (r) => r.data.filter((c) => c.chargeable && !c.expired),
    enabled: !!props.portal || (!!props.customerId && can('PC900')),
  });
  const cards = saved.data ?? [];
  const primary = cards.find((c) => c.isPrimary) ?? cards[0];
  const [picked, setPicked] = useState<string | null>(null);
  const choice = picked ?? (primary ? primary.id : NEW);
  const [save, setSave] = useState(false);
  const [simToken, setSimToken] = useState(SIM_CARDS[0]!.token);
  const inner = useRef<CardFieldHandle>(null);
  const gateway = config.data?.gateway;

  const stripePromise = useMemo(
    () =>
      gateway === 'stripe' && config.data?.publishableKey
        ? stripeFor(config.data.publishableKey)
        : null,
    [gateway, config.data?.publishableKey],
  );

  useImperativeHandle(
    ref,
    () => ({
      collect: async () => {
        if (choice !== NEW) return { savedCardId: choice, save: false };
        if (gateway === 'simulated') return { paymentMethodId: simToken, save };
        if ((gateway === 'stripe' || gateway === 'authorizenet') && inner.current)
          return inner.current.collect();
        throw new Error('Card payments are not connected.');
      },
    }),
    [choice, gateway, simToken, save],
  );

  if (!gateway) return null;
  if (gateway === 'none')
    return (
      <Alert severity="warning">
        Card payments are not connected. An administrator connects them in Settings &gt;
        Connections.
      </Alert>
    );
  const canSave = !!props.portal || (!!props.customerId && can(CARD_CODES.saveCard));
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {cards.length ? (
        <TextField select label="Card" value={choice} onChange={(e) => setPicked(e.target.value)}>
          {cards.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              Saved {c.cardType} ending {c.last4} ({String(c.expMonth).padStart(2, '0')}/{c.expYear}
              ){c.isPrimary ? ', primary' : ''}
            </MenuItem>
          ))}
          <MenuItem value={NEW}>A new card</MenuItem>
        </TextField>
      ) : null}
      {choice === NEW && gateway === 'simulated' ? (
        <>
          <Alert severity="info">
            Card payments are simulated on this system: no card is charged. Pick a test card.
          </Alert>
          <TextField
            select
            label="Test card"
            value={simToken}
            onChange={(e) => setSimToken(e.target.value)}
          >
            {SIM_CARDS.map((c) => (
              <MenuItem key={c.token} value={c.token}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
        </>
      ) : null}
      {gateway === 'stripe' && stripePromise ? (
        <Elements stripe={stripePromise}>
          <StripeCard handle={inner} props={{ ...props, choice, save }} />
        </Elements>
      ) : null}
      {gateway === 'authorizenet' && config.data?.authorizeNet && choice === NEW ? (
        <AuthorizeNetCard handle={inner} config={config.data.authorizeNet} save={save} />
      ) : null}
      {choice === NEW && canSave ? (
        <FormControlLabel
          control={<Checkbox checked={save} onChange={(e) => setSave(e.target.checked)} />}
          label="Save this card for later payments and Auto Pay"
        />
      ) : null}
      {props.error ? (
        <Typography color="error" variant="body2" role="alert">
          {props.error}
        </Typography>
      ) : null}
    </Box>
  );
});
